import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import { useTagResolution, usePatchTags } from '@/hooks/usePatching'
import { PATCH_ENVIRONMENTS, type PatchEnvironment, type VmTag } from '@/types/patching'
import type { CampaignFormValues, TargetFormValues, TargetMode } from '@/utils/patchValidation'
import { Field, FieldErrorList, type ErrorsFor } from './FormParts'
import styles from './Patching.module.css'

interface TargetFieldsProps {
  values: CampaignFormValues
  onChange: (patch: Partial<CampaignFormValues>) => void
  errorsFor: ErrorsFor
}

const ENV_HINT: Record<PatchEnvironment, string> = {
  dev: 'Required. Patching starts here.',
  uat: 'Optional. Promoted only after DEV is accepted.',
  prod: 'Optional. Requires a change ticket, a maintenance window and a recovery point at execution time.',
}

interface TagsState {
  tags: VmTag[]
  loading: boolean
  error: string | null
}

interface EnvTargetProps {
  env: PatchEnvironment
  target: TargetFormValues
  onChange: (next: TargetFormValues) => void
  errorsFor: ErrorsFor
  tagsState: TagsState
}

/** The read-only preview of what a tag holds right now: the VMs, where they are, and anything that would stop a campaign. */
function TagPreview({ env, tag, ambiguous }: { env: PatchEnvironment; tag: VmTag | undefined; ambiguous: VmTag[] }) {
  const resolution = useTagResolution(ambiguous.length === 0 ? tag?.id : undefined)
  const label = env.toUpperCase()
  if (ambiguous.length > 0) {
    return (
      <div className={clsx(styles.alert, styles.alertWarning)} role="alert">
        <AlertTriangle size={16} className={styles.alertIcon} aria-hidden="true" />
        <span>
          A tag named “{ambiguous[0].name}” exists in {ambiguous.length} Azure connections ({ambiguous.map((t) => t.connection_name).join(', ')}).
          Rename one of them, so a campaign can tell them apart.
        </span>
      </div>
    )
  }
  if (!tag) return null
  if (resolution.isLoading) return <p className={styles.muted} role="status">Loading the VMs in {tag.name}…</p>
  if (resolution.isError || !resolution.data) {
    return <div className={clsx(styles.alert, styles.alertError)} role="alert">{resolution.error?.message ?? 'The VMs in this tag could not be loaded.'}</div>
  }
  const { vms, ready, blockers, resource_groups: groups } = resolution.data
  return (
    <section className={styles.stack} aria-label={`${label} tag preview`} data-testid={`tag-preview-${env}`}>
      <span className={styles.label}>
        VMs in this tag right now <span className={styles.muted}>(read-only)</span>
      </span>
      <div className={styles.tableWrap}>
        <table className={clsx(styles.table, styles.previewTable)}>
          <thead>
            <tr>
              <th>VM</th>
              <th>Resource group</th>
              <th>Region</th>
              <th>Power</th>
            </tr>
          </thead>
          <tbody>
            {vms.map((vm) => (
              <tr key={vm.id}>
                <td>
                  <span className={styles.mono}>{vm.vm_name}</span>
                  {vm.problems.map((problem) => (
                    <span key={problem} className={clsx(styles.cellSub, styles.problemText)}>{problem}</span>
                  ))}
                </td>
                <td>{vm.resource_group}</td>
                <td>{vm.region}</td>
                <td>{vm.power_state}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ready ? (
        <span className={styles.hint}>
          {vms.length} VM{vms.length === 1 ? '' : 's'} in {groups.length === 1 ? groups[0] : `${groups.length} resource groups (${groups.join(', ')})`}. They are copied into the campaign when you create it; later changes to the tag do not affect it.
          A VM that is stopped or gone by the time you execute is left out and reported.
        </span>
      ) : (
        <div className={clsx(styles.alert, styles.alertWarning)} role="alert">
          <AlertTriangle size={16} className={styles.alertIcon} aria-hidden="true" />
          <span>
            A campaign cannot be created from this tag yet:
            <ul className={styles.notesList}>
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </span>
        </div>
      )}
    </section>
  )
}

function EnvTarget({ env, target, onChange, errorsFor, tagsState }: EnvTargetProps) {
  const label = env.toUpperCase()
  const rgPath = `environments.${env}.resource_group`
  const vmPath = `environments.${env}.vm_names`
  const tagPath = `environments.${env}.tag_name`
  const rgErrors = errorsFor(rgPath)
  const vmErrors = errorsFor(vmPath)
  const tagErrors = errorsFor(tagPath)
  const optional = env !== 'dev'
  const byName = tagsState.tags.filter((tag) => tag.name === target.tag_name)
  const names = [...new Set(tagsState.tags.map((tag) => tag.name))]

  const setVm = (index: number, value: string) =>
    onChange({ ...target, vm_names: target.vm_names.map((v, i) => (i === index ? value : v)) })

  return (
    <fieldset className={styles.stack} style={{ border: '1px solid var(--aptean-border)', borderRadius: 8, padding: 14, margin: 0 }}>
      <legend className={styles.label}>
        {optional ? (
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={target.included}
              onChange={(e) => onChange({ ...target, included: e.target.checked })}
              aria-label={`Include ${label}`}
            />
            <span>Include {label}</span>
          </label>
        ) : (
          label
        )}
      </legend>
      <span className={styles.hint}>{ENV_HINT[env]}</span>
      <FieldErrorList messages={errorsFor(`environments.${env}`).filter((m) => !rgErrors.includes(m) && !vmErrors.includes(m) && !tagErrors.includes(m))} />

      {target.included && (
        <fieldset className={styles.radioGroup} aria-label={`${label} target by`}>
          <legend className={styles.label}>Target by</legend>
          {([
            ['explicit', 'Explicit VM list'],
            ['tag', 'Tag'],
          ] as [TargetMode, string][]).map(([mode, text]) => (
            <label key={mode} className={styles.checkboxRow}>
              <input
                type="radio"
                name={`target-mode-${env}`}
                value={mode}
                checked={target.mode === mode}
                onChange={() => onChange({ ...target, mode })}
                aria-label={`${label} ${text}`}
              />
              <span>{text}</span>
            </label>
          ))}
        </fieldset>
      )}

      {target.included && target.mode === 'tag' && (
        <>
          <Field
            id={`tag-${env}`}
            label={`${label} tag`}
            required
            errors={tagErrors}
            hint="The VMs come from the tag and are copied into the campaign when it is created. The subscription comes from the environment configuration, never from this form."
          >
            <select
              id={`tag-${env}`}
              className={clsx(styles.select, tagErrors.length > 0 && styles.inputError)}
              value={target.tag_name}
              disabled={tagsState.loading}
              onChange={(e) => onChange({ ...target, tag_name: e.target.value })}
            >
              <option value="">{tagsState.loading ? 'Loading tags…' : '— Select a tag —'}</option>
              {names.map((name) => {
                const same = tagsState.tags.filter((tag) => tag.name === name)
                const text =
                  same.length > 1
                    ? `${name} · in ${same.length} connections (rename one first)`
                    : `${name} · ${same[0].connection_name} · ${same[0].member_count} VM${same[0].member_count === 1 ? '' : 's'}`
                return (
                  <option key={name} value={name}>{text}</option>
                )
              })}
            </select>
          </Field>
          {tagsState.error && <div className={clsx(styles.alert, styles.alertError)} role="alert">{tagsState.error}</div>}
          {!tagsState.loading && !tagsState.error && names.length === 0 && (
            <span className={styles.hint}>There are no tags yet. Select VMs on the Azure inventory page and use “Add tags” to make one.</span>
          )}
          {target.tag_name !== '' && byName.length === 0 && !tagsState.loading && !tagsState.error && (
            <span className={styles.problemText}>No tag is named “{target.tag_name}”.</span>
          )}
          <TagPreview env={env} tag={byName.length === 1 ? byName[0] : undefined} ambiguous={byName.length > 1 ? byName : []} />
        </>
      )}

      {target.included && target.mode === 'explicit' && (
        <>
          <Field id={`rg-${env}`} label={`${label} resource group`} required errors={rgErrors} hint="Explicit name — no wildcards. The subscription comes from the environment configuration, never from this form.">
            <input
              id={`rg-${env}`}
              className={clsx(styles.input, rgErrors.length > 0 && styles.inputError)}
              value={target.resource_group}
              onChange={(e) => onChange({ ...target, resource_group: e.target.value })}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>

          <div className={styles.field}>
            <span className={styles.label}>
              {label} VM names <span aria-hidden="true">*</span>
            </span>
            {target.vm_names.map((vm, index) => (
              <div key={index} className={styles.inlineRow}>
                <input
                  className={clsx(styles.input, vmErrors.length > 0 && styles.inputError)}
                  aria-label={`${label} VM name ${index + 1}`}
                  value={vm}
                  onChange={(e) => setVm(index, e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className={styles.chip}
                  aria-label={`Remove ${label} VM ${index + 1}`}
                  disabled={target.vm_names.length === 1 && env === 'dev'}
                  onClick={() => onChange({ ...target, vm_names: target.vm_names.filter((_, i) => i !== index) })}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <div>
              <button
                type="button"
                className={styles.chip}
                onClick={() => onChange({ ...target, vm_names: [...target.vm_names, ''] })}
              >
                <Plus size={12} /> Add {label} VM
              </button>
            </div>
            <span className={styles.hint}>One explicit VM name per row. Wildcards, blanks, whitespace and duplicates are rejected.</span>
            <FieldErrorList messages={vmErrors} />
          </div>
        </>
      )}
    </fieldset>
  )
}

/** Per-environment targets: an explicit resource group + VM names, or the name of a tag. Never asks for a subscription. */
export function TargetFields({ values, onChange, errorsFor }: TargetFieldsProps) {
  // The tags are only fetched once some environment asks for them, so an explicit-list campaign makes no extra request.
  const wantsTags = PATCH_ENVIRONMENTS.some((env) => values.environments[env].included && values.environments[env].mode === 'tag')
  const query = usePatchTags(wantsTags)
  const tagsState: TagsState = { tags: query.data ?? [], loading: wantsTags && query.isLoading, error: query.error?.message ?? null }
  return (
    <div className={styles.stack}>
      {PATCH_ENVIRONMENTS.map((env) => (
        <EnvTarget
          key={env}
          env={env}
          target={values.environments[env]}
          errorsFor={errorsFor}
          tagsState={tagsState}
          onChange={(next) => onChange({ environments: { ...values.environments, [env]: next } })}
        />
      ))}
    </div>
  )
}
